import { networkCore, orbitProjects, type OrbitProject, type ProjectId } from "./data/projects";
import { revealCoreImage } from "./core-image";
import "./styles/global.css";

type Selection = "network" | ProjectId;
type IntroPhase = "sun" | "sky" | "planets" | "settle" | "title" | "tagline" | "ready";
type IntroPlayback = "full" | "quick";
type IntroRevealState = "idle" | "quick-prep" | "quick-active";

interface OrbitRuntime {
  project: OrbitProject;
  button: HTMLButtonElement;
  ring: SVGEllipseElement | null;
  connector: SVGLineElement | null;
  slotAngle: number;
  radius: number;
  collisionRadius: number;
  x: number;
  y: number;
}

interface OrbitLayout {
  width: number;
  height: number;
  offsetLeft: number;
  offsetTop: number;
  centerX: number;
  centerY: number;
  coreRadius: number;
  orbitSquash: number;
  edgeMargin: number;
  coreMargin: number;
  compact: boolean;
}

interface Star {
  x: number;
  y: number;
  size: number;
  tone: number;
  color: string;
  twinkle: number;
  speed: number;
  revealDelay: number;
  revealDuration: number;
}

interface IntroSequence {
  totalMs: number;
  sunMs: number;
  skyDelayMs: number;
  skyMs: number;
  planetsDelayMs: number;
  planetStaggerMs: number;
  planetPopMs: number;
  settleDelayMs: number;
  settleMs: number;
  titleDelayMs: number;
  titleMs: number;
  taglineDelayMs: number;
  taglineMs: number;
}

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Missing #app mount point");
}

const mount = app;
const baseUrl = import.meta.env.BASE_URL;
const selectionOrder: Selection[] = ["network", ...orbitProjects.map((project) => project.id)];
const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
const constellationSpeed = 0.000088;
const collisionIterations = 3;
const selectionPauseMs = 260;
const scrollPauseMs = 140;
const introFreshLoadThresholdPx = 24;
const quickRevealDurationMs = 220;
const introBootstrapDelayMs = 120;
const introPhaseOrder: IntroPhase[] = ["sun", "sky", "planets", "settle", "title", "tagline", "ready"];
const INTRO_SEQUENCE: IntroSequence = {
  totalMs: 5000,
  sunMs: 520,
  skyDelayMs: 320,
  skyMs: 2100,
  planetsDelayMs: 1740,
  planetStaggerMs: 135,
  planetPopMs: 720,
  settleDelayMs: 3220,
  settleMs: 1120,
  titleDelayMs: 3660,
  titleMs: 920,
  taglineDelayMs: 4320,
  taglineMs: 520
};

let selected: Selection = "network";
let panelExpanded = false;
let shellElement: HTMLElement | null = null;
let detailPanelElement: HTMLElement | null = null;
let orbitMapElement: HTMLElement | null = null;
let orbitSceneElement: HTMLElement | null = null;
let coreButtonElement: HTMLButtonElement | null = null;
let selectedProjectLayerElement: HTMLElement | null = null;
let selectedProjectClone: HTMLElement | null = null;
let orbitRuntime: OrbitRuntime[] = [];
let lastOrbitLayout: OrbitLayout | null = null;
let layoutDirty = true;
let sceneFrozen = false;
let lockedScrollY = 0;
let motionPauseUntil = 0;
let introPhase: IntroPhase = "sun";
let introReady = false;
let introPlayback: IntroPlayback = "full";
let introRevealState: IntroRevealState = "idle";
let introTimeoutIds: number[] = [];
let introStartedAt = 0;
let introQuickRevealFrameId = 0;
let introQuickRevealTimeoutId = 0;
let introBootstrapResolved = false;
let introBootstrapTimeoutId = 0;

function resolveIntroSequence(sequence: IntroSequence): IntroSequence {
  const rawTailMs = Math.max(
    sequence.sunMs,
    sequence.skyDelayMs + sequence.skyMs,
    sequence.planetsDelayMs + sequence.planetPopMs + Math.max(orbitProjects.length - 1, 0) * sequence.planetStaggerMs,
    sequence.settleDelayMs + sequence.settleMs,
    sequence.titleDelayMs + sequence.titleMs,
    sequence.taglineDelayMs + sequence.taglineMs,
    1
  );
  const scale = sequence.totalMs / rawTailMs;
  const scaleTiming = (value: number): number => Math.round(value * scale);

  return {
    totalMs: Math.round(sequence.totalMs),
    sunMs: scaleTiming(sequence.sunMs),
    skyDelayMs: scaleTiming(sequence.skyDelayMs),
    skyMs: scaleTiming(sequence.skyMs),
    planetsDelayMs: scaleTiming(sequence.planetsDelayMs),
    planetStaggerMs: scaleTiming(sequence.planetStaggerMs),
    planetPopMs: scaleTiming(sequence.planetPopMs),
    settleDelayMs: scaleTiming(sequence.settleDelayMs),
    settleMs: scaleTiming(sequence.settleMs),
    titleDelayMs: scaleTiming(sequence.titleDelayMs),
    titleMs: scaleTiming(sequence.titleMs),
    taglineDelayMs: scaleTiming(sequence.taglineDelayMs),
    taglineMs: scaleTiming(sequence.taglineMs)
  };
}

const introSequence = resolveIntroSequence(INTRO_SEQUENCE);

function assetPath(path: string): string {
  return `${baseUrl}${path}`;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function easeOutCubic(value: number): number {
  return 1 - Math.pow(1 - value, 3);
}

function hasReachedIntroPhase(phase: IntroPhase): boolean {
  return introPhaseOrder.indexOf(introPhase) >= introPhaseOrder.indexOf(phase);
}

function getIntroProgress(time: number, delayMs: number, durationMs: number): number {
  if (introStartedAt === 0) {
    return 0;
  }

  return clamp((time - introStartedAt - delayMs) / Math.max(durationMs, 1), 0, 1);
}

function isAtIntroFreshLoadPosition(useFreshLoadTolerance: boolean = false): boolean {
  return window.scrollY <= (useFreshLoadTolerance ? introFreshLoadThresholdPx : 0);
}

function pauseSceneMotion(durationMs: number = selectionPauseMs): void {
  motionPauseUntil = Math.max(motionPauseUntil, performance.now() + durationMs);
}

function isSceneMotionPaused(time: number): boolean {
  return sceneFrozen || document.hidden || time < motionPauseUntil;
}

function statusLabel(status: OrbitProject["status"]): string {
  return status === "live" ? "Live" : "Coming soon";
}

function getProject(id: Selection): OrbitProject | undefined {
  return orbitProjects.find((project) => project.id === id);
}

function isProjectSelection(value: Selection): value is ProjectId {
  return value !== "network";
}

function renderOrbitRings(): string {
  const rings = orbitProjects
    .map((project) => {
      const radiusX = (project.orbitRadius / 100) * 900;
      const radiusY = (project.orbitRadius / 100) * 650;

      return `<ellipse class="orbit-ring" data-ring="${project.id}" cx="450" cy="325" rx="${radiusX.toFixed(
        1
      )}" ry="${radiusY.toFixed(1)}" style="--project-color: ${project.color}" />`;
    })
    .join("");
  const connectors = orbitProjects
    .map(
      (project) =>
        `<line class="orbit-line" data-connector="${project.id}" x1="450" y1="325" x2="450" y2="325" style="--project-color: ${project.color}" />`
    )
    .join("");

  return `
    <svg class="orbit-svg" viewBox="0 0 900 650" preserveAspectRatio="none" aria-hidden="true">
      ${connectors}
      ${rings}
    </svg>
  `;
}

function renderProjectNodes(): string {
  return orbitProjects
    .map(
      (project, index) => `
        <button
          type="button"
          class="project-node"
          data-select="${project.id}"
          data-project-id="${project.id}"
          aria-label="Show ${escapeHtml(project.name)}"
          aria-pressed="false"
          style="--project-color: ${project.color}; --intro-order: ${index}"
        >
          <span class="planet-avatar" aria-hidden="true">
            <img src="${assetPath(project.icon)}" alt="" loading="eager" />
          </span>
          <span class="project-label">
            <span class="project-name">${escapeHtml(project.id === "fortrust" ? "Fortrust" : project.id)}</span>
            <span class="project-status status-${project.status}">${statusLabel(project.status)}</span>
          </span>
        </button>
      `
    )
    .join("");
}

function renderProofItems(): string {
  return networkCore.proof
    .map(
      (item) => `
        <div class="proof-item">
          <strong>${escapeHtml(item.value)}</strong>
          <span>${escapeHtml(item.label)}</span>
        </div>
      `
    )
    .join("");
}

function renderNetworkStory(): string {
  return `
    <section class="network-story" aria-labelledby="network-story-title">
      <div class="story-title-block">
        <p class="story-kicker">${escapeHtml(networkCore.tagline)}</p>
        <h2 id="network-story-title">
          <span>Peaceful Vanilla</span>
          <span>Network</span>
        </h2>
      </div>

      <article class="network-story-card" aria-label="${escapeHtml(networkCore.name)} overview">
        <div class="story-card-copy">
          <h3>A small independent ecosystem for community, play, privacy, and connection.</h3>
          <p>
            ${escapeHtml(networkCore.description)}
          </p>
        </div>

        <div class="story-feature-grid" aria-label="What Peaceful Vanilla Network does">
          <section>
            <span>01</span>
            <h4>The Network that connects</h4>
            <p>We are the team behind Club, Hytale, Chat, Space, and more. One single discoverable network.</p>
          </section>
          <section>
            <span>02</span>
            <h4>The Network that protects</h4>
            <p>We keep evil big tech out. We believe in a family-and-friends way of working and ethical entrepreneuring. No big-corps involved.</p>
          </section>
          <section>
            <span>03</span>
            <h4>The Network that builds privacy-first</h4>
            <p>Favors self-hosted, low-friction community tools over invasive platforms.</p>
          </section>
          <section>
            <span>04</span>
            <h4>The Network that gives weird ideas room</h4>
            <p>We love to explore new weird ideas related to gaming and social platforms.</p>
          </section>
        </div>
      </article>
    </section>
  `;
}

function renderCorePanel(): string {
  const projectLinks = orbitProjects
    .map(
      (project) => `
        <button type="button" data-select="${project.id}" style="--project-color: ${project.color}">
          <strong>${escapeHtml(project.name)}</strong>
          ${project.tagline ? `<span>${escapeHtml(project.tagline)}</span>` : ""}
        </button>
      `
    )
    .join("");

  return `
    <div class="panel-inner">
      <div class="panel-topline">
        <span class="status-pill" style="--status-color: var(--accent-soft)">Network core</span>
      </div>
      <div class="panel-copy">
        <p class="panel-kicker">${escapeHtml(networkCore.name)}</p>
        <h2 class="panel-title">${escapeHtml(networkCore.tagline)}</h2>
        <p class="panel-description">${escapeHtml(networkCore.description)}</p>
      </div>
      <div class="principles">
        ${networkCore.principles.map((principle) => `<span>${escapeHtml(principle)}</span>`).join("")}
      </div>
      <div class="panel-metrics" aria-label="Network proof points">${renderProofItems()}</div>
      <div class="network-links">${projectLinks}</div>
    </div>
  `;
}

function renderProjectPanel(project: OrbitProject): string {
  const ctas = [
    project.primaryCta
      ? `<a class="panel-cta primary" href="${project.primaryCta.href}" target="_blank" rel="noreferrer">${escapeHtml(
          project.primaryCta.label
        )}</a>`
      : "",
    project.secondaryCta
      ? `<a class="panel-cta" href="${project.secondaryCta.href}" target="_blank" rel="noreferrer">${escapeHtml(
          project.secondaryCta.label
        )}</a>`
      : ""
  ]
    .filter(Boolean)
    .join("");

  const destinationNote = project.destinationNote
    ? `<div class="coming-soon-note">${escapeHtml(project.destinationNote)}</div>`
    : "";
  const actions = ctas || destinationNote;
  const badges = project.tagline
    ? `<div class="principles">
        <span>${escapeHtml(project.tagline)}</span>
        <span>${escapeHtml(project.status === "live" ? "Active destination" : "Network preview")}</span>
      </div>`
    : "";

  return `
    <div class="panel-inner">
      <div class="panel-topline">
        <span class="status-pill" style="--status-color: ${
          project.status === "live" ? "var(--success)" : "var(--stone)"
        }">${statusLabel(project.status)}</span>
        <button type="button" class="panel-close" data-select="network" aria-label="Return to network overview">x</button>
      </div>
      <div class="panel-copy">
        <p class="panel-kicker">${escapeHtml(project.domainLabel)}</p>
        <h2 class="panel-title">${escapeHtml(project.name)}</h2>
        <p class="panel-description">${escapeHtml(project.description)}</p>
      </div>
      ${badges}
      ${actions ? `<div class="cta-stack">${actions}</div>` : ""}
    </div>
  `;
}

function renderPanel(): string {
  const project = getProject(selected);
  return project ? renderProjectPanel(project) : renderCorePanel();
}

function renderShell(): void {
  mount.innerHTML = `
    <div
      class="app-shell"
      style="--project-color: var(--accent)"
      data-selected="network"
      data-panel-expanded="false"
      data-intro-phase="sun"
      data-intro-ready="false"
      data-intro-playback="full"
      data-intro-reveal="idle"
    >
      <div class="intro-surface" aria-hidden="true"></div>
      <div class="cosmos-backdrop" aria-hidden="true">
        <canvas class="starfield" id="starfield" aria-hidden="true"></canvas>
        <div class="cosmos-gradient"></div>
      </div>

      <main class="command-deck">
        <section class="universe-card" aria-labelledby="network-title">
          <h1 id="network-title" class="visually-hidden">${escapeHtml(networkCore.name)}</h1>

          <div class="orbit-map" id="orbit-map" tabindex="0" role="application" aria-label="Interactive Peaceful Vanilla Network orbit">
            <div class="orbit-scene" id="orbit-scene">
              ${renderOrbitRings()}
              <button type="button" class="core-button" data-select="network" aria-label="Show ${escapeHtml(networkCore.name)} overview">
                <span class="core-aura" aria-hidden="true"></span>
                <img src="${assetPath(networkCore.icon)}" alt="" width="768" height="768" loading="eager" decoding="async" fetchpriority="high" />
                <span class="core-label">
                  <strong>${escapeHtml(networkCore.name.toUpperCase())}</strong>
                  <span>${escapeHtml(networkCore.tagline)}</span>
                </span>
              </button>
              <div class="project-layer" id="project-layer">
                ${renderProjectNodes()}
              </div>
            </div>
          </div>
        </section>

        <aside class="detail-panel is-collapsed" id="detail-panel" aria-live="polite" aria-expanded="false" aria-hidden="true" inert>
          ${renderPanel()}
        </aside>
      </main>

      ${renderNetworkStory()}

      <div class="selected-project-layer" aria-hidden="true"></div>
    </div>
  `;
}

function cacheRuntimeElements(): void {
  shellElement = mount.querySelector<HTMLElement>(".app-shell");
  detailPanelElement = mount.querySelector<HTMLElement>("#detail-panel");
  orbitMapElement = mount.querySelector<HTMLElement>("#orbit-map");
  orbitSceneElement = mount.querySelector<HTMLElement>("#orbit-scene");
  coreButtonElement = mount.querySelector<HTMLButtonElement>(".core-button");
  selectedProjectLayerElement = mount.querySelector<HTMLElement>(".selected-project-layer");
  orbitRuntime = orbitProjects.map((project, index) => {
    const button = mount.querySelector<HTMLButtonElement>(`button[data-project-id="${project.id}"]`);

    if (!button) {
      throw new Error(`Missing orbit node for ${project.id}`);
    }

    return {
      project,
      button,
      ring: mount.querySelector<SVGEllipseElement>(`[data-ring="${project.id}"]`),
      connector: mount.querySelector<SVGLineElement>(`[data-connector="${project.id}"]`),
      slotAngle: -Math.PI / 2 + (index * Math.PI * 2) / orbitProjects.length,
      radius: 0,
      collisionRadius: 0,
      x: 0,
      y: 0
    };
  });
}

function applyIntroSequenceVariables(): void {
  if (!shellElement) {
    return;
  }

  shellElement.style.setProperty("--intro-total-ms", `${introSequence.totalMs}ms`);
  shellElement.style.setProperty("--intro-sun-ms", `${introSequence.sunMs}ms`);
  shellElement.style.setProperty("--intro-sky-delay-ms", `${introSequence.skyDelayMs}ms`);
  shellElement.style.setProperty("--intro-sky-ms", `${introSequence.skyMs}ms`);
  shellElement.style.setProperty("--intro-planets-delay-ms", `${introSequence.planetsDelayMs}ms`);
  shellElement.style.setProperty("--intro-planet-stagger-ms", `${introSequence.planetStaggerMs}ms`);
  shellElement.style.setProperty("--intro-planet-pop-ms", `${introSequence.planetPopMs}ms`);
  shellElement.style.setProperty("--intro-settle-delay-ms", `${introSequence.settleDelayMs}ms`);
  shellElement.style.setProperty("--intro-settle-ms", `${introSequence.settleMs}ms`);
  shellElement.style.setProperty("--intro-title-delay-ms", `${introSequence.titleDelayMs}ms`);
  shellElement.style.setProperty("--intro-title-ms", `${introSequence.titleMs}ms`);
  shellElement.style.setProperty("--intro-tagline-delay-ms", `${introSequence.taglineDelayMs}ms`);
  shellElement.style.setProperty("--intro-tagline-ms", `${introSequence.taglineMs}ms`);
}

function syncIntroState(): void {
  shellElement?.setAttribute("data-intro-phase", introPhase);
  shellElement?.setAttribute("data-intro-ready", String(introReady));
  shellElement?.setAttribute("data-intro-playback", introPlayback);
  shellElement?.setAttribute("data-intro-reveal", introRevealState);

  if (orbitMapElement) {
    orbitMapElement.toggleAttribute("inert", !introReady);
  }
}

function setIntroPhase(nextPhase: IntroPhase): void {
  introPhase = nextPhase;
  introReady = nextPhase === "ready";
  syncIntroState();
  syncDocumentScrollLock();
}

function clearIntroSequence(): void {
  for (const timeoutId of introTimeoutIds) {
    window.clearTimeout(timeoutId);
  }

  introTimeoutIds = [];
}

function clearQuickRevealSequence(): void {
  if (introQuickRevealFrameId) {
    window.cancelAnimationFrame(introQuickRevealFrameId);
    introQuickRevealFrameId = 0;
  }

  if (introQuickRevealTimeoutId) {
    window.clearTimeout(introQuickRevealTimeoutId);
    introQuickRevealTimeoutId = 0;
  }
}

function clearIntroBootstrapTimeout(): void {
  if (introBootstrapTimeoutId) {
    window.clearTimeout(introBootstrapTimeoutId);
    introBootstrapTimeoutId = 0;
  }
}

function startIntroSequence(): void {
  clearIntroSequence();
  clearQuickRevealSequence();
  introPlayback = "full";
  introRevealState = "idle";
  introStartedAt = performance.now();
  setIntroPhase("sun");

  const schedule: Array<{ phase: IntroPhase; at: number }> = [
    { phase: "sky", at: introSequence.skyDelayMs },
    { phase: "planets", at: introSequence.planetsDelayMs },
    { phase: "settle", at: introSequence.settleDelayMs },
    { phase: "title", at: introSequence.titleDelayMs },
    { phase: "tagline", at: introSequence.taglineDelayMs },
    { phase: "ready", at: introSequence.totalMs }
  ];

  introTimeoutIds = schedule.map(({ phase, at }) =>
    window.setTimeout(() => {
      setIntroPhase(phase);
    }, at)
  );
}

function startQuickRevealSequence(): void {
  clearIntroSequence();
  clearQuickRevealSequence();
  introPlayback = "quick";
  introRevealState = "quick-prep";
  introStartedAt = 0;
  setIntroPhase("ready");

  if (!shellElement) {
    return;
  }

  shellElement.getBoundingClientRect();
  introQuickRevealFrameId = window.requestAnimationFrame(() => {
    introQuickRevealFrameId = 0;
    introRevealState = "quick-active";
    syncIntroState();

    introQuickRevealTimeoutId = window.setTimeout(() => {
      introQuickRevealTimeoutId = 0;
      introRevealState = "idle";
      syncIntroState();
    }, quickRevealDurationMs + 40);
  });
}

function resolveInitialIntroPlayback(useFreshLoadTolerance: boolean = false): void {
  if (introBootstrapResolved) {
    return;
  }

  introBootstrapResolved = true;
  clearIntroBootstrapTimeout();
  setLayoutDirty();

  if (isAtIntroFreshLoadPosition(useFreshLoadTolerance)) {
    startIntroSequence();
    return;
  }

  startQuickRevealSequence();
}

function setLayoutDirty(): void {
  layoutDirty = true;
}

function observeLayoutChanges(): void {
  window.addEventListener("resize", setLayoutDirty, { passive: true });
  window.addEventListener("orientationchange", setLayoutDirty, { passive: true });
  window.addEventListener(
    "scroll",
    () => {
      pauseSceneMotion(scrollPauseMs);
    },
    { passive: true }
  );

  if ("ResizeObserver" in window) {
    const resizeObserver = new ResizeObserver(setLayoutDirty);

    if (orbitMapElement) {
      resizeObserver.observe(orbitMapElement);
    }

    if (coreButtonElement) {
      resizeObserver.observe(coreButtonElement);
    }

    for (const runtime of orbitRuntime) {
      resizeObserver.observe(runtime.button);
    }
  }

  document.fonts?.ready.then(setLayoutDirty).catch(() => undefined);

  for (const image of mount.querySelectorAll<HTMLImageElement>("img")) {
    if (!image.complete) {
      image.addEventListener("load", setLayoutDirty, { once: true });
      image.addEventListener("error", setLayoutDirty, { once: true });
    }
  }
}

function getSelectedShowcasePosition(layout: OrbitLayout): { x: number; y: number } {
  const desktop = window.innerWidth >= 920;
  const x = desktop ? clamp(window.innerWidth * 0.3, 240, 520) : window.innerWidth * 0.5;
  const y = desktop
    ? clamp(window.innerHeight * 0.5, 170, Math.max(170, window.innerHeight - 170))
    : clamp(window.innerHeight * 0.31, 132, Math.max(132, window.innerHeight * 0.38));

  return {
    x: Number.isFinite(x) ? x : layout.offsetLeft + layout.centerX,
    y: Number.isFinite(y) ? y : layout.offsetTop + layout.centerY
  };
}

function updateActiveElements(projectColor: string): void {
  coreButtonElement?.classList.toggle("is-active", selected === "network");

  for (const runtime of orbitRuntime) {
    const isActive = runtime.project.id === selected;
    runtime.button.classList.toggle("is-active", isActive);
    runtime.button.setAttribute("aria-pressed", String(isActive));
    runtime.ring?.classList.toggle("is-active", isActive);
    runtime.ring?.style.setProperty("--project-color", projectColor);
    runtime.connector?.classList.toggle("is-active", isActive);
    runtime.connector?.style.setProperty("--project-color", projectColor);
  }

  detailPanelElement?.querySelectorAll<HTMLElement>("[data-select]").forEach((element) => {
    element.classList.toggle("is-active", element.dataset.select === selected);
  });
}

function updatePanelState(): void {
  const panel = detailPanelElement;

  if (!panel) {
    return;
  }

  if (panelExpanded && panel.dataset.projectId !== selected) {
    panel.innerHTML = renderPanel();
    panel.dataset.projectId = selected;
  }
  panel.toggleAttribute("inert", !panelExpanded);
  panel.setAttribute("aria-expanded", String(panelExpanded));
  panel.setAttribute("aria-hidden", String(!panelExpanded));

  panel.classList.toggle("is-expanded", panelExpanded);
  panel.classList.toggle("is-collapsed", !panelExpanded);
}

function syncSelectedProjectClone(): void {
  const layer = selectedProjectLayerElement;
  const activeRuntime = isProjectSelection(selected)
    ? orbitRuntime.find((runtime) => runtime.project.id === selected)
    : undefined;

  if (!layer || !panelExpanded || !activeRuntime) {
    selectedProjectClone = null;
    selectedProjectLayerElement?.replaceChildren();
    selectedProjectLayerElement?.classList.remove("has-selection");
    return;
  }

  if (selectedProjectClone?.dataset.projectId !== activeRuntime.project.id) {
    const clone = activeRuntime.button.cloneNode(true) as HTMLButtonElement;
    clone.classList.add("selected-project-node", "is-active");
    clone.removeAttribute("data-select");
    clone.removeAttribute("aria-label");
    clone.setAttribute("tabindex", "-1");
    clone.setAttribute("aria-hidden", "true");
    clone.dataset.projectId = activeRuntime.project.id;
    clone.style.setProperty("--project-color", activeRuntime.project.color);
    layer.replaceChildren(clone);
    selectedProjectClone = clone;
  }

  if (selectedProjectClone && lastOrbitLayout) {
    const showcase = getSelectedShowcasePosition(lastOrbitLayout);
    selectedProjectClone.style.setProperty("--tx", `${showcase.x.toFixed(1)}px`);
    selectedProjectClone.style.setProperty("--ty", `${showcase.y.toFixed(1)}px`);
  }

  layer.classList.add("has-selection");
}

function lockDocumentScroll(): void {
  if (document.body.classList.contains("is-detail-scroll-locked")) {
    return;
  }

  lockedScrollY = window.scrollY;
  document.documentElement.classList.add("is-detail-scroll-locked");
  document.body.classList.add("is-detail-scroll-locked");
  document.body.style.position = "fixed";
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
}

function unlockDocumentScroll(): void {
  if (!document.body.classList.contains("is-detail-scroll-locked")) {
    return;
  }

  document.documentElement.classList.remove("is-detail-scroll-locked");
  document.body.classList.remove("is-detail-scroll-locked");
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  window.scrollTo(0, lockedScrollY);
}

function syncDocumentScrollLock(): void {
  if (panelExpanded) {
    lockDocumentScroll();
    return;
  }

  unlockDocumentScroll();
}

function setSelection(next: Selection): void {
  if (next === selected && introReady) {
    return;
  }

  selected = next;
  panelExpanded = isProjectSelection(selected);
  sceneFrozen = panelExpanded;
  pauseSceneMotion(panelExpanded ? selectionPauseMs : 180);

  const project = getProject(selected);
  const projectColor = project?.color ?? "#ff9500";
  shellElement?.setAttribute("data-selected", selected);
  shellElement?.setAttribute("data-panel-expanded", String(panelExpanded));
  shellElement?.classList.toggle("is-focused", panelExpanded);
  shellElement?.classList.toggle("is-panel-expanded", panelExpanded);
  shellElement?.classList.toggle("is-scene-frozen", sceneFrozen);
  shellElement?.style.setProperty("--project-color", projectColor);
  detailPanelElement?.style.setProperty("--project-color", projectColor);

  updateActiveElements(projectColor);
  updatePanelState();
  syncSelectedProjectClone();
  syncDocumentScrollLock();
  setLayoutDirty();
}

function selectByOffset(offset: number): void {
  const currentIndex = selectionOrder.indexOf(selected);
  const nextIndex = (currentIndex + offset + selectionOrder.length) % selectionOrder.length;
  const nextSelection = selectionOrder[nextIndex];

  if (nextSelection) {
    setSelection(nextSelection);
  }
}

function wireInteractions(): void {
  document.addEventListener("click", (event) => {
    if (!introReady) {
      return;
    }

    const clickedElement = event.target instanceof Element ? event.target : null;
    const target = clickedElement?.closest<HTMLElement>("[data-select]") ?? null;
    const next = target?.dataset.select as Selection | undefined;

    if (next && selectionOrder.includes(next)) {
      setSelection(next);
      return;
    }

    if (panelExpanded && clickedElement && !detailPanelElement?.contains(clickedElement)) {
      setSelection("network");
    }
  });

  orbitMapElement?.addEventListener("keydown", (event) => {
    if (!introReady) {
      if (event.key === "Tab") {
        event.preventDefault();
      }

      return;
    }

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      selectByOffset(1);
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      selectByOffset(-1);
    }

    if (event.key === "Escape" || event.key === "Home") {
      event.preventDefault();
      setSelection("network");
    }
  });

  document.addEventListener("keydown", (event) => {
    if (!introReady) {
      if (event.key === "Tab") {
        event.preventDefault();
      }

      return;
    }

    if (event.key === "Escape" && panelExpanded) {
      event.preventDefault();
      setSelection("network");
    }
  });
  detailPanelElement?.addEventListener(
    "scroll",
    () => {
      pauseSceneMotion(scrollPauseMs);
    },
    { passive: true }
  );
}

function refreshOrbitLayout(): OrbitLayout | null {
  const orbitMap = orbitMapElement;

  if (!orbitMap) {
    return null;
  }

  const mapRect = orbitMap.getBoundingClientRect();
  const mapWidth = Math.max(orbitMap.clientWidth || mapRect.width, 1);
  const mapHeight = Math.max(orbitMap.clientHeight || mapRect.height, 1);
  const aspectRatio = mapWidth / mapHeight;
  const compact = mapWidth < 760;
  const wideBias = clamp((aspectRatio - 1.26) / 0.54, 0, 1);
  const tallBias = clamp((0.92 - aspectRatio) / 0.28, 0, 1);
  const desktopNudgeX = compact ? 0 : Math.min(mapWidth * 0.014, 18);
  const mobileLiftY = compact ? Math.min(mapHeight * 0.03, 20) : 0;
  const sceneOffsetX = wideBias * Math.min(mapWidth * 0.082, 104) + desktopNudgeX;
  const sceneOffsetY = -tallBias * Math.min(mapHeight * 0.068, 56) - mobileLiftY;
  const centerX = mapWidth / 2;
  const centerY = mapHeight / 2;
  const coreRadius = (coreButtonElement?.offsetWidth ?? Math.min(mapWidth, mapHeight) * 0.24) / 2;
  const edgeMargin = compact ? 8 : 18;
  const coreMargin = compact ? 10 : 26;
  const orbitSquash = compact ? 1.18 : 0.88;
  const orbitRadiusBase = Math.min(mapWidth, mapHeight) * (compact ? 0.0108 : 0.0114);

  shellElement?.style.setProperty("--orbit-bias-x", `${sceneOffsetX.toFixed(1)}px`);
  shellElement?.style.setProperty("--orbit-bias-y", `${sceneOffsetY.toFixed(1)}px`);
  orbitSceneElement?.style.setProperty("--scene-settle-x", `${sceneOffsetX.toFixed(1)}px`);
  orbitSceneElement?.style.setProperty("--scene-settle-y", `${sceneOffsetY.toFixed(1)}px`);

  const layout: OrbitLayout = {
    width: mapWidth,
    height: mapHeight,
    offsetLeft: mapRect.left,
    offsetTop: mapRect.top,
    centerX,
    centerY,
    coreRadius,
    orbitSquash,
    edgeMargin,
    coreMargin,
    compact
  };

  lastOrbitLayout = layout;

  for (const runtime of orbitRuntime) {
    const selectedScale = selected === runtime.project.id ? (compact ? 1.08 : 1.22) : 1;
    const halo = selected === runtime.project.id ? (compact ? 16 : 36) : compact ? 10 : 22;
    const nodeWidth = Math.max(runtime.button.offsetWidth, 1) * selectedScale + halo;
    const nodeHeight = Math.max(runtime.button.offsetHeight, 1) * selectedScale + halo;
    const nodeRadius = Math.max(nodeWidth, nodeHeight) / 2;
    const maxRadiusX = Math.max(0, centerX - nodeRadius - edgeMargin);
    const maxRadiusY = Math.max(0, (centerY - nodeRadius - edgeMargin) / orbitSquash);
    const maxRadius = Math.min(maxRadiusX, maxRadiusY);
    const minRadius = coreRadius + nodeRadius + coreMargin;
    const desiredRadius = runtime.project.orbitRadius * orbitRadiusBase;

    runtime.collisionRadius = nodeRadius;
    runtime.radius = maxRadius >= minRadius ? clamp(desiredRadius, minRadius, maxRadius) : maxRadius;

    if (runtime.ring) {
      runtime.ring.setAttribute("rx", ((runtime.radius / mapWidth) * 900).toFixed(1));
      runtime.ring.setAttribute("ry", (((runtime.radius * orbitSquash) / mapHeight) * 650).toFixed(1));
    }
  }

  layoutDirty = false;
  return layout;
}

function keepRuntimeInBounds(runtime: OrbitRuntime, layout: OrbitLayout): void {
  const minX = runtime.collisionRadius + layout.edgeMargin;
  const maxX = layout.width - runtime.collisionRadius - layout.edgeMargin;
  const minY = runtime.collisionRadius + layout.edgeMargin;
  const maxY = layout.height - runtime.collisionRadius - layout.edgeMargin;
  runtime.x = clamp(runtime.x, Math.min(minX, maxX), Math.max(minX, maxX));
  runtime.y = clamp(runtime.y, Math.min(minY, maxY), Math.max(minY, maxY));

  const dx = runtime.x - layout.centerX;
  const dy = runtime.y - layout.centerY;
  const minCoreDistance = layout.coreRadius + runtime.collisionRadius + layout.coreMargin;
  const distance = Math.hypot(dx, dy);

  if (distance < minCoreDistance) {
    const angle = distance > 0.001 ? Math.atan2(dy, dx) : runtime.slotAngle;
    runtime.x = layout.centerX + Math.cos(angle) * minCoreDistance;
    runtime.y = layout.centerY + Math.sin(angle) * minCoreDistance;
    runtime.x = clamp(runtime.x, Math.min(minX, maxX), Math.max(minX, maxX));
    runtime.y = clamp(runtime.y, Math.min(minY, maxY), Math.max(minY, maxY));
  }
}

function solveOrbitCollisions(layout: OrbitLayout): void {
  for (let iteration = 0; iteration < collisionIterations; iteration += 1) {
    for (const runtime of orbitRuntime) {
      keepRuntimeInBounds(runtime, layout);
    }

    for (let index = 0; index < orbitRuntime.length; index += 1) {
      const current = orbitRuntime[index];

      if (!current) {
        continue;
      }

      for (let nextIndex = index + 1; nextIndex < orbitRuntime.length; nextIndex += 1) {
        const next = orbitRuntime[nextIndex];

        if (!next) {
          continue;
        }

        const dx = next.x - current.x;
        const dy = next.y - current.y;
        const minDistance = current.collisionRadius + next.collisionRadius;
        const distance = Math.hypot(dx, dy);

        if (distance >= minDistance) {
          continue;
        }

        const fallbackAngle = current.slotAngle + (nextIndex - index) * 0.73;
        const normalX = distance > 0.001 ? dx / distance : Math.cos(fallbackAngle);
        const normalY = distance > 0.001 ? dy / distance : Math.sin(fallbackAngle);
        const push = (minDistance - distance) / 2 + 0.5;

        current.x -= normalX * push;
        current.y -= normalY * push;
        next.x += normalX * push;
        next.y += normalY * push;

        keepRuntimeInBounds(current, layout);
        keepRuntimeInBounds(next, layout);
      }
    }
  }
}

function applyOrbitPositions(layout: OrbitLayout): void {
  const activeRuntime = isProjectSelection(selected)
    ? orbitRuntime.find((runtime) => runtime.project.id === selected)
    : undefined;

  for (const runtime of orbitRuntime) {
    runtime.button.style.setProperty("--tx", `${runtime.x.toFixed(1)}px`);
    runtime.button.style.setProperty("--ty", `${runtime.y.toFixed(1)}px`);

    if (runtime.connector) {
      runtime.connector.setAttribute("x1", "450");
      runtime.connector.setAttribute("y1", "325");
      runtime.connector.setAttribute("x2", ((runtime.x / layout.width) * 900).toFixed(1));
      runtime.connector.setAttribute("y2", ((runtime.y / layout.height) * 650).toFixed(1));
    }
  }

  if (selectedProjectClone && activeRuntime && panelExpanded) {
    const showcase = getSelectedShowcasePosition(layout);
    selectedProjectClone.style.setProperty("--tx", `${showcase.x.toFixed(1)}px`);
    selectedProjectClone.style.setProperty("--ty", `${showcase.y.toFixed(1)}px`);
  }
}

function animateOrbit(): void {
  let orbitTime = 0;
  let motionAccumulator = 0;
  let previousFrameTime: number | undefined;
  let layout = refreshOrbitLayout();
  let previousDynamicScene = false;

  function frame(time: number): void {
    const needsLayoutRefresh = layoutDirty || !layout;

    if (needsLayoutRefresh) {
      layout = refreshOrbitLayout();
    }

    if (!layout) {
      window.requestAnimationFrame(frame);
      return;
    }

    const delta = previousFrameTime === undefined ? 16.67 : Math.min(time - previousFrameTime, 40);
    previousFrameTime = time;
    const dynamicScene = hasReachedIntroPhase("planets") && !isSceneMotionPaused(time);
    const orbitFrameMs = reducedMotionQuery.matches ? 80 : 32;

    if (dynamicScene) {
      motionAccumulator += delta;

      if (motionAccumulator < orbitFrameMs) {
        previousDynamicScene = dynamicScene;
        window.requestAnimationFrame(frame);
        return;
      }

      orbitTime += motionAccumulator;
      motionAccumulator = 0;

      const baseAngle = orbitTime * (reducedMotionQuery.matches ? constellationSpeed * 0.28 : constellationSpeed);

      for (const runtime of orbitRuntime) {
        const angle = baseAngle + runtime.slotAngle;
        runtime.x = layout.centerX + Math.cos(angle) * runtime.radius;
        runtime.y = layout.centerY + Math.sin(angle) * runtime.radius * layout.orbitSquash;
      }

      solveOrbitCollisions(layout);
      applyOrbitPositions(layout);
    } else if (needsLayoutRefresh || dynamicScene !== previousDynamicScene) {
      applyOrbitPositions(layout);
    }

    previousDynamicScene = dynamicScene;
    window.requestAnimationFrame(frame);
  }

  window.requestAnimationFrame(frame);
}

function createStars(count: number): Star[] {
  return Array.from({ length: count }, () => ({
    x: Math.random(),
    y: Math.random(),
    size: 0.45 + Math.random() * 1.9,
    tone: Math.random(),
    color: Math.random() > 0.82 ? "255, 149, 0" : Math.random() > 0.66 ? "80, 124, 190" : "247, 249, 252",
    twinkle: Math.random() * Math.PI * 2,
    speed: 0.0007 + Math.random() * 0.0012,
    revealDelay: Math.random() * 1050,
    revealDuration: 180 + Math.random() * 760
  }));
}

function startStarfield(): void {
  const canvas = document.querySelector<HTMLCanvasElement>("#starfield");
  const context = canvas?.getContext("2d");

  if (!canvas || !context) {
    return;
  }

  const starCanvas = canvas;
  const starContext = context;
  const stars = createStars(window.innerWidth < 760 ? 68 : 96);
  let width = 0;
  let height = 0;
  let lastDraw = 0;

  function resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
    width = window.innerWidth;
    height = window.innerHeight;
    starCanvas.width = Math.floor(width * dpr);
    starCanvas.height = Math.floor(height * dpr);
    starCanvas.style.width = `${width}px`;
    starCanvas.style.height = `${height}px`;
    starContext.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw(0);
  }

  function draw(time: number): void {
    starContext.clearRect(0, 0, width, height);

    const titleGlowProgress = easeOutCubic(getIntroProgress(time, introSequence.titleDelayMs, introSequence.titleMs + 820));

    if (titleGlowProgress > 0) {
      const gradient = starContext.createRadialGradient(width * 0.5, height * 0.45, 0, width * 0.5, height * 0.45, width * 0.72);
      gradient.addColorStop(0, `rgba(255, 149, 0, ${(0.075 * titleGlowProgress).toFixed(3)})`);
      gradient.addColorStop(0.34, `rgba(80, 124, 190, ${(0.05 * titleGlowProgress).toFixed(3)})`);
      gradient.addColorStop(1, "rgba(3, 5, 10, 0)");
      starContext.fillStyle = gradient;
      starContext.fillRect(0, 0, width, height);
    }

    for (const star of stars) {
      const twinkle = reducedMotionQuery.matches
        ? 0.72 + Math.sin(time * star.speed * 0.22 + star.twinkle) * 0.05
        : 0.54 + Math.sin(time * star.speed * 0.72 + star.twinkle) * 0.18;
      const revealProgress = easeOutCubic(
        clamp((time - introStartedAt - introSequence.titleDelayMs - star.revealDelay) / star.revealDuration, 0, 1)
      );

      if (revealProgress <= 0) {
        continue;
      }

      const alpha = clamp((twinkle + (star.tone > 0.78 ? 0.14 : 0)) * revealProgress, 0.08, 0.8);

      starContext.beginPath();
      starContext.fillStyle = `rgba(${star.color}, ${alpha.toFixed(3)})`;
      starContext.arc(star.x * width, star.y * height, star.size, 0, Math.PI * 2);
      starContext.fill();
    }
  }

  function tick(time: number): void {
    const dynamicScene =
      ((introPlayback === "full" && hasReachedIntroPhase("title")) ||
        (introPlayback === "quick" && introRevealState !== "quick-prep")) &&
      !isSceneMotionPaused(time);
    const framePauseMs = introReady ? (reducedMotionQuery.matches ? 180 : 110) : reducedMotionQuery.matches ? 160 : 95;

    if (dynamicScene && time - lastDraw >= framePauseMs) {
      lastDraw = time;
      draw(time);
    }

    window.requestAnimationFrame(tick);
  }

  window.addEventListener("resize", resize, { passive: true });
  resize();
  window.requestAnimationFrame(tick);
}

renderShell();
cacheRuntimeElements();
const coreImage = mount.querySelector<HTMLImageElement>(".core-button img");
if (coreImage) {
  void revealCoreImage(coreImage);
}
applyIntroSequenceVariables();
observeLayoutChanges();
wireInteractions();
setSelection("network");
animateOrbit();
startStarfield();
window.addEventListener(
  "pageshow",
  () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        resolveInitialIntroPlayback(true);
      });
    });
  },
  { once: true }
);
introBootstrapTimeoutId = window.setTimeout(() => {
  resolveInitialIntroPlayback(true);
}, introBootstrapDelayMs);
